/**
 * Trigger State Broadcaster
 *
 * Provides utilities for triggers and popups to broadcast their state
 * to the admin bar debug panel via custom events.
 *
 * Broadcasting is disabled unless the admin bar is present to avoid
 * unnecessary overhead in production.
 */

import type { TriggerType, ScrollDepthUnit } from '../../shared/types/popup';

// ============================================================================
// Admin Bar Detection
// ============================================================================

/**
 * Check if the admin bar debug panel is present.
 * We cache this check since it won't change during page lifecycle.
 */
let adminBarPresent: boolean | null = null;

function isAdminBarPresent(): boolean {
	if (adminBarPresent === null) {
		// Check for the admin bar config that PHP injects
		adminBarPresent =
			typeof window !== 'undefined' &&
			'gpPopupsAdminBarConfig' in window &&
			window.gpPopupsAdminBarConfig !== undefined;
	}
	return adminBarPresent;
}

// ============================================================================
// Trigger State Types
// ============================================================================

/**
 * State of a trigger
 */
export type TriggerStateType =
	| 'waiting' // Trigger active, waiting for condition
	| 'preloading' // Preload has started
	| 'preloaded' // Iframe ready, waiting for trigger
	| 'triggered'; // Trigger condition met

/**
 * Progress information for triggers that have measurable progress
 */
export interface TriggerProgress {
	/** For timeout triggers: milliseconds remaining */
	remainingMs?: number;
	/** For timeout triggers: total milliseconds */
	totalMs?: number;

	/** For scroll depth triggers: current scroll value */
	currentValue?: number;
	/** For scroll depth triggers: target scroll value */
	targetValue?: number;
	/** For scroll depth triggers: unit of measurement */
	unit?: ScrollDepthUnit;

	/** For button triggers: the CSS selector being watched */
	selector?: string;
}

/**
 * Complete trigger state for broadcasting
 */
export interface TriggerState {
	feedId: number;
	triggerType: TriggerType;
	state: TriggerStateType;
	progress?: TriggerProgress;
}

// ============================================================================
// Popup Lifecycle State Types
// ============================================================================

/**
 * Popup lifecycle state
 */
export type PopupLifecycleState =
	| 'skipped' // Targeting failed or dismissed
	| 'waiting' // Waiting for trigger
	| 'preloading' // Iframe loading
	| 'preloaded' // Iframe ready
	| 'triggered' // Trigger fired
	| 'open' // Popup visible
	| 'dismissed' // User closed
	| 'submitted'; // Form submitted

/**
 * Popup state for broadcasting
 */
export interface PopupState {
	feedId: number;
	state: PopupLifecycleState;
	isReady: boolean;
	isOpen: boolean;
}

// ============================================================================
// Event Names
// ============================================================================

export const TRIGGER_STATE_EVENT = 'gp-popups:trigger-state';
export const POPUP_STATE_EVENT = 'gp-popups:popup-state';

// ============================================================================
// Broadcasting Functions
// ============================================================================

/**
 * Broadcast trigger state to the admin bar debug panel.
 * This is a no-op if the admin bar is not present.
 */
export function broadcastTriggerState(state: TriggerState): void {
	if (!isAdminBarPresent()) {
		return;
	}

	window.dispatchEvent(
		new CustomEvent(TRIGGER_STATE_EVENT, {
			detail: state,
		})
	);
}

/**
 * Broadcast popup lifecycle state to the admin bar debug panel.
 * This is a no-op if the admin bar is not present.
 */
export function broadcastPopupState(state: PopupState): void {
	if (!isAdminBarPresent()) {
		return;
	}

	window.dispatchEvent(
		new CustomEvent(POPUP_STATE_EVENT, {
			detail: state,
		})
	);
}

// ============================================================================
// Utility Class for Managing Trigger State
// ============================================================================

/**
 * Helper class for managing and broadcasting trigger state.
 * Handles throttling and cleanup automatically.
 *
 * All operations are no-ops if the admin bar is not present.
 */
export class TriggerStateBroadcaster {
	private feedId: number;
	private triggerType: TriggerType;
	private currentState: TriggerStateType = 'waiting';
	private progress: TriggerProgress = {};
	private intervalId: ReturnType<typeof setInterval> | null = null;
	private lastBroadcastTime = 0;
	private throttleMs: number;
	private enabled: boolean;

	constructor(
		feedId: number,
		triggerType: TriggerType,
		throttleMs: number = 100
	) {
		this.feedId = feedId;
		this.triggerType = triggerType;
		this.throttleMs = throttleMs;
		// Cache the check at construction time
		this.enabled = isAdminBarPresent();
	}

	/**
	 * Check if broadcasting is enabled (admin bar is present)
	 */
	public isEnabled(): boolean {
		return this.enabled;
	}

	/**
	 * Update the trigger state and broadcast
	 */
	public setState(state: TriggerStateType): void {
		if (!this.enabled) {
			return;
		}

		this.currentState = state;
		this.broadcast();
	}

	/**
	 * Update progress and broadcast (throttled)
	 */
	public setProgress(progress: Partial<TriggerProgress>): void {
		if (!this.enabled) {
			return;
		}

		this.progress = { ...this.progress, ...progress };
		this.broadcastThrottled();
	}

	/**
	 * Start a countdown timer that broadcasts remaining time.
	 * No-op if admin bar is not present.
	 */
	public startCountdown(totalMs: number): void {
		if (!this.enabled) {
			return;
		}

		this.progress = { totalMs, remainingMs: totalMs };
		const startTime = Date.now();

		// Clear any existing interval
		this.stopCountdown();

		// Update every 100ms
		this.intervalId = setInterval(() => {
			const elapsed = Date.now() - startTime;
			const remaining = Math.max(0, totalMs - elapsed);
			this.progress.remainingMs = remaining;
			this.broadcast();

			if (remaining <= 0) {
				this.stopCountdown();
			}
		}, 100);

		// Initial broadcast
		this.broadcast();
	}

	/**
	 * Stop the countdown timer
	 */
	public stopCountdown(): void {
		if (this.intervalId !== null) {
			clearInterval(this.intervalId);
			this.intervalId = null;
		}
	}

	/**
	 * Broadcast current state immediately
	 */
	public broadcast(): void {
		if (!this.enabled) {
			return;
		}

		broadcastTriggerState({
			feedId: this.feedId,
			triggerType: this.triggerType,
			state: this.currentState,
			progress: { ...this.progress },
		});
		this.lastBroadcastTime = Date.now();
	}

	/**
	 * Broadcast current state (throttled)
	 */
	private broadcastThrottled(): void {
		const now = Date.now();
		if (now - this.lastBroadcastTime >= this.throttleMs) {
			this.broadcast();
		}
	}

	/**
	 * Clean up resources
	 */
	public destroy(): void {
		this.stopCountdown();
	}
}
